/**
 * CareAssessment — Unified App
 * All screens in correct order with fully reusable components
 *
 * Screen Order:
 *  1. StartCareAssessmentScreen
 *  2. ConsentScreen
 *  3. NewPatientScreen
 *  4. ProfileScreen
 *  5. HeightWeightScreen
 *  6. BasicLifeStageScreen
 *  7. EntryReasonScreen
 *  8. ConditionSpecificIntakeScreen
 *  9. SafetyCheckScreen
 * 10. AdaptiveQuestionnaireScreen
 * 11. RecommendationScreen (Done)
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScreenHeaderDFO } from "../components/HealthAssessment/SharedUI";
import config from './HealthAssessment.json';
import { BASE_URL } from "../api/apiClient";
console.log("Imported config:", config);
// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  purple900: "#1A0A3C",
  purple800: "#2D1466",
  purple700: "#3B1A8C",
  purple600: "#4C24A8",
  purple500: "#5B2EBF",
  purpleBtn: "#3D1A8A",
  purpleLight: "#EDE9FB",
  purpleBorder: "#6B48D3",
  purpleTag: "#E8E3FA",
  dark: "#0F0A1E",
  heading: "#1A1033",
  body: "#374151",
  sub: "#6B7280",
  muted: "#9CA3AF",
  border: "#E5E7EB",
  borderFocus: "#6B48D3",
  pageBg: "#FFFFFF",
  lavenderBg: "#F4F1FD",
  cardBg: "#FFFFFF",
  inputBg: "#FFFFFF",
  trustBg: "#EDE9FB",
  infoBg: "#EFF6FF",
  infoBorder: "#BFDBFE",
  infoText: "#1E40AF",
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
  pink: "#EC4899",
  pinkLight: "#FFF0F5",
  pinkBorder: "#FBCFE8",
};

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE PRIMITIVE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

export const IconCircle = ({ children, size = 36, bg = T.purpleLight }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%", background: bg,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, fontSize: size * 0.42,
  }}>
    {children}
  </div>
);

export const StageBadge = ({ label }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 6,
    background: T.purpleTag, borderRadius: 20, padding: "4px 12px",
    fontSize: 11, fontWeight: 600, color: T.purple700,
  }}>
    🌸 {label}
  </span>
);

export const SectionLabel = ({ children }) => (
  <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.08em", textTransform: "uppercase", padding: "10px 0 4px", margin: 0 }}>
    {children}
  </p>
);

export const Chip = ({ label, selected, onClick }) => (
  <button onClick={onClick} style={{
    padding: "7px 14px", borderRadius: 999, fontSize: 11, fontWeight: selected ? 600 : 400,
    border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: selected ? T.purpleLight : "#fff",
    color: selected ? T.purple700 : T.sub,
    cursor: "pointer", transition: "all 0.15s",
  }}>
    {label}
  </button>
);

export const SymptomChip = ({ label, selected, onClick }) => {
  const icon = SYMPTOM_ICONS[label] || "•";
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
      borderRadius: 12, textAlign: "left", position: "relative",
      border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
      background: selected ? T.purpleLight : "#fff",
      color: selected ? T.purple700 : T.body,
      cursor: "pointer", fontSize: 12, fontWeight: selected ? 600 : 400,
      transition: "all 0.18s",
    }}>
      <IconCircle size={32} bg={selected ? "#D8D0F8" : "#F3F4F6"}>{icon}</IconCircle>
      <span style={{ flex: 1 }}>{label}</span>
      {selected && (
        <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.purple700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="10" height="8" viewBox="0 0 12 9" fill="none">
            <path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </button>
  );
};

export const CareCheckbox = ({ checked, onChange }) => (
  <div onClick={onChange} style={{
    width: 20, height: 20, borderRadius: 5, flexShrink: 0, cursor: "pointer",
    border: checked ? "none" : `2px solid ${T.border}`,
    background: checked ? T.purple700 : "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
  }}>
    {checked && <svg width="10" height="8" viewBox="0 0 12 9" fill="none"><path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
  </div>
);

export const Toggle = ({ on, onChange }) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: "pointer",
    background: on ? T.purple700 : T.border, position: "relative", transition: "background 0.2s",
  }}>
    <div style={{
      width: 16, height: 16, borderRadius: "50%", background: "#fff",
      position: "absolute", top: 3, left: on ? 21 : 3, transition: "left 0.2s",
    }} />
  </div>
);

export const InfoBox = ({ children }) => (
  <div style={{ background: T.infoBg, border: `1px solid ${T.infoBorder}`, borderRadius: 8, padding: "8px 10px" }}>
    <p style={{ fontSize: 10, color: T.infoText, margin: 0, lineHeight: 1.5 }}>ℹ️ {children}</p>
  </div>
);

export const PurpleInfoBox = ({ icon, title, desc }) => (
  <div style={{ background: T.lavenderBg, borderRadius: 8, padding: "8px 10px", display: "flex", gap: 8 }}>
    <span style={{ fontSize: 14 }}>{icon}</span>
    <div>
      <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, margin: "0 0 2px" }}>{title}</p>
      <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>{desc}</p>
    </div>
  </div>
);

export const TrustBar = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 12, padding: "8px 0 2px" }}>
    {["🔒 Secure", "👩‍⚕️ Expert-led", "🌸 Personalised"].map(item => (
      <span key={item} style={{ fontSize: 10, color: T.muted, fontWeight: 500 }}>{item}</span>
    ))}
  </div>
);

export const FeatureItem = ({ icon, title, desc }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
    <div style={{ width: 28, height: 28, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{icon}</div>
    <div>
      <p style={{ fontWeight: 600, fontSize: 11, color: T.heading, margin: "0 0 2px" }}>{title}</p>
      <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>{desc}</p>
    </div>
  </div>
);

export const StyledSelect = ({ value, onChange, options, placeholder }) => (
  <div style={{ position: "relative" }}>
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      width: "100%", padding: "10px 36px 10px 12px", borderRadius: 8, appearance: "none",
      border: `1.5px solid ${value ? T.purpleBorder : T.border}`,
      fontSize: 12, color: value ? T.heading : T.muted,
      background: "#fff", cursor: "pointer", outline: "none",
    }}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.muted, fontSize: 12 }}>▾</span>
  </div>
);

export const YesNoBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: "6px 16px", borderRadius: 8, fontSize: 11, fontWeight: active ? 700 : 400,
    border: active ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: active ? T.purpleLight : "#fff",
    color: active ? T.purple700 : T.sub, cursor: "pointer", transition: "all 0.15s",
  }}>
    {label}
  </button>
);

export const YesNo = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 8 }}>
    {["Yes", "No"].map(opt => (
      <button key={opt} onClick={() => onChange(opt)} style={{
        flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 12,
        border: value === opt ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
        background: value === opt ? T.purpleLight : "#fff",
        color: value === opt ? T.purple700 : T.sub,
        fontWeight: value === opt ? 700 : 400, cursor: "pointer", transition: "all 0.15s",
      }}>
        {opt}
      </button>
    ))}
  </div>
);

export const  BtnBack = ({ onClick ,isExit}) => (
  <button onClick={onClick}
  //  style={{
  //   display: "flex", alignItems: "center", gap: 5, padding: "7px 14px",
  //   borderRadius: 8, fontSize: 12, fontWeight: 600,
  //   border: `1.5px solid ${T.border}`, background: "#fff", color: T.body, cursor: "pointer",
  // }}
  className="btn-transparent"
  >
  {isExit?" Exit":"Back"}
  </button>
);

export const BtnContinue = ({ onClick, disabled = false, label = "Continue ", wide = false, color = T.purpleBtn }) => (
  <button onClick={onClick} disabled={disabled} 
  // style={{
  //   display: "flex", alignItems: "center", gap: 8,
  //   paddingLeft: 18, paddingRight: 8, paddingTop: 8, paddingBottom: 8,
  //   borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#fff", border: "none",
  //   background: disabled ? "#A78BFA" : color,
  //   cursor: disabled ? "not-allowed" : "pointer",
  //   width: wide ? "100%" : undefined,
  //   justifyContent: "space-between", opacity: disabled ? 0.75 : 1, transition: "background 0.15s",
  // }}
  className="btn-primary"
  >
    <span style={{ flex: 1, textAlign: "left", whiteSpace: "nowrap" }}>{label}</span>
    {/* <span style={{ width: 30, height: 30, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>→</span> */}
  </button>
);

export const BtnAskNavigator = ({ onClick }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", gap: 6, padding: "6px 14px",
    borderRadius: 20, fontSize: 11, fontWeight: 600,
    border: `1.5px solid ${T.purpleBorder}`, background: T.purpleLight,
    color: T.purple700, cursor: "pointer",
  }}>
    💬 Ask a Care Navigator
  </button>
);

export const FooterNote = ({ children }) => (
  <p style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.muted, margin: 0, whiteSpace: "nowrap" }}>
    🔒 {children}
  </p>
);

export const FooterBar = ({ left, center, right }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 0 0", marginTop: 10, borderTop: `1px solid ${T.border}`,
  }}>
    <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>{left}</div>
    <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>{center}</div>
    <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>{right}</div>
  </div>
);

export const PageWrap = ({ children, bg = T.pageBg }) => (
  <div style={{
    minHeight: "100vh",
    background: "linear-gradient(135deg,#e8f4f8 0%,#f0eeff 50%,#e8f4f8 100%)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "48px 16px 24px", fontFamily: "system-ui, -apple-system, sans-serif",
    boxSizing: "border-box",
  }}>
    <div style={{
      width: "100%", maxWidth: 960, background: bg, borderRadius: 20,
      padding: "24px 32px 20px", boxShadow: "0 4px 40px rgba(61,26,138,0.09)",
      border: `1px solid ${T.border}`, boxSizing: "border-box",
    }}>
      {children}
    </div>
  </div>
);

export const RightCard = ({ children, style = {} }) => (
  <div style={{
    background: "#fff", borderRadius: 14, padding: 14, border: `1px solid ${T.border}`,
    display: "flex", flexDirection: "column", gap: 10, ...style,
  }}>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SYMPTOM ICONS MAP
// ─────────────────────────────────────────────────────────────────────────────
const SYMPTOM_ICONS = {
  "Irregular periods": "🗓️", "Acne": "🫧", "Facial hair growth": "✨",
  "Weight gain": "⚖️", "Hair thinning": "💆", "Pelvic pain": "💜",
  "Bloating": "🫁", "Breast tenderness": "🌸", "Mood swings": "🧠",
  "Headaches/migraines": "🤕", "Nausea": "🤢", "Fatigue": "😴",
  "None of the above": "✅", "Lower abdomen": "📍", "Lower back": "🔙",
  "Pelvic floor / between the hip bones": "💜", "During sex (deep pain)": "❤️",
  "During urination": "💧", "During bowel movements": "⚠️",
  "Hot flushes": "🌡️", "Night sweats": "💧", "Sleep disturbances": "🌙",
  "Reduced sex drive": "❤️", "Vaginal dryness": "💧",
  "Joint pain or stiffness": "🦴",
  "Unusual discharge (colour, smell, or texture)": "🔬",
  "Itching or burning": "🔥", "Pain during sex": "❤️",
  "Pain or burning when urinating": "💧", "Fever or chills": "🌡️",
  "Pelvic pain or pressure": "💜",
};
const getSymptomIcon = (label) => SYMPTOM_ICONS[label] || "•";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const Assessment_OPTIONS = [
  { value: "pcos", label: "PCOS / Irregular Period Assessment", emoji: "🔬", desc: "For irregular cycles, hormonal symptoms, acne, weight changes, or cycle concerns" },
  { value: "pregnancy", label: "Pregnancy Start Assessment", emoji: "🤰", desc: "For pregnancy confirmation, prenatal care, symptoms, scans, and routine check-ups" },
  { value: "fertility", label: "Fertility / Preconception Assessment", emoji: "🌿", desc: "For trying to conceive, fertility planning, cycle tracking, and preconception support" },
  { value: "postpartum", label: "Postpartum Assessment", emoji: "👶", desc: "For recovery after delivery, breastfeeding, mood, bleeding, and follow-up care" },
  { value: "menopause", label: "Perimenopause / Menopause Assessment", emoji: "🌡️", desc: "For hot flashes, cycle changes, sleep, mood, and menopause wellness" },
  { value: "general", label: "General Women's Health", emoji: "❤️", desc: "For preventive check-ups, pelvic pain, infection symptoms, and overall wellness" },
];

const Assessment_LABELS = {
  pcos: "PCOS / Irregular Period Assessment", pregnancy: "Pregnancy Start Assessment",
  fertility: "Fertility / Preconception Assessment", postpartum: "Postpartum Assessment",
  menopause: "Perimenopause / Menopause Assessment", general: "General Women's Health",
};
const Assessment_EMOJIS = { pcos: "🔬", pregnancy: "🤰", fertility: "🌿", postpartum: "👶", menopause: "🌡️", general: "❤️" };

const AGE_OPTIONS = ["18–25", "26–35", "36–45", "46–55", "56+"];
const MARITAL_OPTIONS = ["Single", "Married", "Divorced", "Prefer not to say"];
const OCCUPATION_OPTIONS = ["Student", "Employed (Office)", "Employed (Field)", "Self-Employed", "Homemaker", "Retired", "Other"];

const SYMPTOM_MAP = {
  menstrual_health: ["Irregular periods", "Acne", "Facial hair growth", "Weight gain", "Hair thinning", "Pelvic pain", "Bloating", "Mood swings", "Fatigue", "Headaches/migraines"],
  pcos_screening: ["Irregular periods", "Acne", "Facial hair growth", "Weight gain", "Hair thinning", "Pelvic pain", "Bloating", "Mood swings", "Fatigue"],
  pelvic_pain: ["Lower abdomen", "Lower back", "Pelvic floor / between the hip bones", "During sex (deep pain)", "During urination", "During bowel movements", "Bloating", "Nausea", "Fatigue"],
  menopause_symptoms: ["Hot flushes", "Night sweats", "Sleep disturbances", "Mood swings", "Reduced sex drive", "Vaginal dryness", "Joint pain or stiffness", "Fatigue", "Weight gain"],
  infection_gynae: ["Unusual discharge (colour, smell, or texture)", "Itching or burning", "Pain during sex", "Pain or burning when urinating", "Fever or chills", "Pelvic pain or pressure", "Nausea", "Fatigue"],
};

const SAFETY_SYMPTOMS = [
  { label: "Severe pain", icon: "⚡" }, { label: "Heavy bleeding", icon: "🩸" },
  { label: "Dizziness or fainting", icon: "😵" }, { label: "Fever", icon: "🌡️" },
  { label: "Reduced baby movement", icon: "🍼" }, { label: "Severe headache", icon: "🤕" },
  { label: "Shortness of breath", icon: "🫁" }, { label: "None of these", icon: "✅" },
];

// Static entry reason data (normally from agedata.json)
export const ENTRY_REASONS_STATIC = {
  youngAdult: {
    screenTitle: "What brings you in today?",
    AssessmentTitle: "Young Adult Women's Health",
    options: [
      { value: "menstrual_health", emoji: "🗓️", label: "Menstrual Health", desc: "Irregular or painful periods", route: "menstrual_health" },
      { value: "pcos_screening", emoji: "🔬", label: "PCOS Screening", desc: "Hormonal or cycle concerns", route: "pcos_screening" },
      { value: "pelvic_pain", emoji: "💜", label: "Pelvic Pain", desc: "Pain in lower abdomen", route: "pelvic_pain" },
      { value: "infection_gynae", emoji: "🛡️", label: "Infection / STI", desc: "Discharge or discomfort", route: "infection_gynae" },
      { value: "preventive", emoji: "✅", label: "Preventive Check", desc: "Routine wellness visit", route: null },
      { value: "other", emoji: "❓", label: "Something Else", desc: "Other women's health concern", route: null },
    ],
  },
  reproductive: {
    screenTitle: "What brings you in today?",
    AssessmentTitle: "Reproductive Age Women's Health",
    options: [
      { value: "pcos_screening", emoji: "🔬", label: "PCOS / Hormonal", desc: "Cycle or hormonal concerns", route: "pcos_screening" },
      { value: "fertility", emoji: "🌿", label: "Fertility Support", desc: "Trying to conceive", route: null },
      { value: "pelvic_pain", emoji: "💜", label: "Pelvic Pain", desc: "Chronic or acute pelvic pain", route: "pelvic_pain" },
      { value: "pregnancy", emoji: "🤰", label: "Pregnancy", desc: "Confirm or prenatal care", route: null },
      { value: "postpartum", emoji: "👶", label: "Postpartum", desc: "After delivery care", route: null },
      { value: "preventive", emoji: "✅", label: "Preventive Check", desc: "Routine wellness", route: null },
    ],
  },
  perimenopause: {
    screenTitle: "What brings you in today?",
    AssessmentTitle: "Perimenopause / Menopause Care",
    options: [
      { value: "menopause_symptoms", emoji: "🌡️", label: "Menopause Symptoms", desc: "Hot flashes, mood, sleep", route: "menopause_symptoms" },
      { value: "pelvic_pain", emoji: "💜", label: "Pelvic Health", desc: "Pain or discomfort", route: "pelvic_pain" },
      { value: "infection_gynae", emoji: "🛡️", label: "Infection / STI", desc: "Discharge or discomfort", route: "infection_gynae" },
      { value: "preventive", emoji: "✅", label: "Preventive Check", desc: "Routine wellness", route: null },
      { value: "mental_health", emoji: "🧠", label: "Mental Wellbeing", desc: "Mood or anxiety support", route: null },
      { value: "other", emoji: "❓", label: "Something Else", desc: "Other health concern", route: null },
    ],
  },
};

export const getStageKey = (age, marital) => {
  if (age === "46–55" || age === "56+") return "perimenopause";
  if (age === "26–35" || age === "36–45") return "reproductive";
  return "youngAdult";
};

// ─────────────────────────────────────────────────────────────────────────────
// OPTION CARD (shared between Screen 1 & 7)
// ─────────────────────────────────────────────────────────────────────────────
const OptionCard = ({ emoji, label, desc, selected, onClick, badge }) => (
  <button onClick={onClick} style={{
    position: "relative", textAlign: "left", borderRadius: 12, padding: "12px 12px 10px",
    background: selected ? T.purpleLight : T.cardBg,
    border: selected ? `2px solid ${T.purpleBorder}` : `1px solid ${T.border}`,
    cursor: "pointer", display: "flex", flexDirection: "column", transition: "all 0.2s",
  }}>
    {selected && (
      <div style={{ position: "absolute", top: 8, right: 8, width: 20, height: 20, background: T.purple700, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="10" height="8" viewBox="0 0 12 9" fill="none"><path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
    )}
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <IconCircle size={36}>{emoji}</IconCircle>
      <p style={{ fontWeight: 700, fontSize: 11, lineHeight: 1.3, color: T.heading, paddingRight: selected ? 20 : 0, margin: 0 }}>{label}</p>
    </div>
    <p style={{ fontSize: 10, lineHeight: 1.4, color: T.sub, flex: 1, margin: "0 0 6px" }}>{desc}</p>
    {badge && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, color: badge.color, background: badge.bg, alignSelf: "flex-start", marginBottom: 4 }}>{badge.label}</span>}
    {/* <span style={{ fontSize: 11, fontWeight: 600, color: T.purple700 }}>Explore →</span> */}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// ADAPTIVE Q BUILDER
// ─────────────────────────────────────────────────────────────────────────────
function buildAdaptiveQuestions(allData) {
  const { selectedAssessment, selectedRoute, selectedSymptoms = [], duration, severity, tryingToConceive, profile = {}, urgentSymptoms = [] } = allData;
  const qs = [];

  if (selectedAssessment === "pcos" || selectedRoute === "pcos_screening" || selectedSymptoms.includes("Irregular periods")) {
    qs.push({ id: "aq_cycle_gap", q: "How many days apart are your cycles on average?", type: "single", options: ["Less than 21 days", "21–28 days", "29–35 days", "35–90 days", "More than 90 days / absent"] });
    qs.push({ id: "aq_insulin", q: "Do you experience strong sugar cravings or energy crashes after meals?", type: "single", options: ["Frequently", "Sometimes", "Rarely", "Never"] });
  }
  if (selectedSymptoms.includes("Acne") || selectedSymptoms.includes("Facial hair growth")) {
    qs.push({ id: "aq_androgen", q: "Where do you mostly notice acne or unwanted hair growth?", type: "multi", options: ["Chin / jaw", "Upper lip", "Chest", "Back / shoulders", "Abdomen"] });
  }
  if (selectedSymptoms.includes("Weight gain")) {
    qs.push({ id: "aq_weight_change", q: "Roughly how much weight have you gained, and over what period?", type: "single", options: ["Less than 3 kg (< 6 months)", "3–7 kg (6–12 months)", "More than 7 kg", "Not sure"] });
  }
  if (tryingToConceive === "Yes" || selectedAssessment === "fertility" || selectedAssessment === "pregnancy") {
    qs.push({ id: "aq_ttc_months", q: "How long have you been actively trying to conceive?", type: "single", options: ["Just starting", "1–3 months", "4–6 months", "7–12 months", "More than 1 year"] });
    qs.push({ id: "aq_ovulation_tracking", q: "Are you currently tracking ovulation?", type: "single", options: ["Yes — OPK / test strips", "Yes — app-based tracking", "Yes — basal body temperature", "No", "Not sure how to"] });
  }
  if (selectedRoute === "pelvic_pain" || selectedSymptoms.includes("Pelvic pain")) {
    qs.push({ id: "aq_pain_timing", q: "When is pelvic pain worst?", type: "multi", options: ["During menstruation", "Mid-cycle / ovulation", "During / after sex", "During bowel movement", "Constantly / unpredictable"] });
  }
  if (selectedAssessment === "menopause" || selectedRoute === "menopause_symptoms") {
    qs.push({ id: "aq_hrt_interest", q: "Are you open to discussing Hormone Replacement Therapy (HRT)?", type: "single", options: ["Already on HRT", "Open to it", "Prefer natural options only", "Would like more information first", "Not interested"] });
    qs.push({ id: "aq_menopause_impact", q: "Which area of life is most impacted by your symptoms?", type: "single", options: ["Sleep", "Work / concentration", "Relationships / intimacy", "Mood", "Physical activity"] });
  }
  if (selectedRoute === "infection_gynae") {
    qs.push({ id: "aq_infection_type", q: "Which description best matches the discharge or discomfort?", type: "single", options: ["White, thick, clumpy", "Grey/white, thin, fishy odour", "Yellow/green, frothy", "Clear / watery", "Uncertain / none"] });
  }
  if (severity === "Severe" || severity === "Very severe") {
    qs.push({ id: "aq_missed_activities", q: "Have your symptoms caused you to miss work, school, or social activities?", type: "single", options: ["Yes — frequently", "Yes — occasionally", "No — I push through it", "No — symptoms are manageable"] });
  }
  if (duration === "More than 1 year" || duration === "6 months to 1 year") {
    qs.push({ id: "aq_prev_treatment", q: "Have you tried any treatments or medications for this so far?", type: "multi", options: ["Over-the-counter pain relief", "Hormonal contraception", "Prescription medication", "Dietary / lifestyle changes", "Herbal / supplements", "Nothing yet"] });
  }
  if (urgentSymptoms.length > 0 && !urgentSymptoms.includes("None of these")) {
    qs.push({ id: "aq_urgent_started", q: "How long ago did these urgent symptoms start?", type: "single", options: ["Within the last hour", "Today", "Yesterday", "2–3 days ago", "More than 3 days ago"] });
  }
  qs.push({ id: "aq_mood_overall", q: "Over the past 2 weeks, how has your overall mood been?", type: "single", options: ["Mostly positive", "Neutral / okay", "Frequently low or anxious", "Very difficult — struggling"] });
  qs.push({ id: "aq_support", q: "Do you have a support system you can talk to about your health?", type: "single", options: ["Yes — partner / family", "Yes — close friends", "Somewhat", "Not really", "I prefer handling things privately"] });

  return qs.slice(0, 6);
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 1 — Start Care Assessment
// ─────────────────────────────────────────────────────────────────────────────
// export function StartCareAssessmentScreen({ onNext }) {
//   const [selected, setSelected] = useState(null);
//   const navigate=useNavigate(null);

//   return (
//     <PageWrap>
//       <ScreenHeaderDFO/>
//       <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", }}>
//         <div style={{ maxWidth: 480 }}>
//           <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, lineHeight: 1.2, marginBottom: 6 }}>Start Care Assessment</h1>
//           <p style={{ fontSize: 12, color: T.sub, lineHeight: 1.5, marginBottom:"20px"}}>Choose the care path that best matches your current need. We'll guide you step by step.</p>
//         </div>
//         {/* <div style={{ fontSize: 64, marginTop: -8, flexShrink: 0, userSelect: "none" }}>👩‍⚕️</div> */}
//       </div>
//       <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1px solid ${T.border}` }}>
//         <p style={{ fontWeight: 600, fontSize: 12, marginBottom: 10, color: T.heading }}>Choose your Assessment</p>
//         <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
//           {Assessment_OPTIONS.map(opt => (
//             <OptionCard key={opt.value} emoji={opt.emoji} label={opt.label} desc={opt.desc}
//               selected={selected === opt.value} onClick={() => setSelected(opt.value)} />
//           ))}
//         </div>
//         <TrustBar />
//       </div>
//       <FooterBar
//         left={<BtnBack onClick={() => {navigate('/')}} isExit={true} />}
//         center={<FooterNote>You can change your selected Assessment later.</FooterNote>}
//         right={<BtnContinue onClick={() => onNext(selected)} />}
//       />
//     </PageWrap>
//   );
// }


// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 2 — Consent & Privacy
// ─────────────────────────────────────────────────────────────────────────────
 function ConsentScreen({ onNext, onBack }) {
  const [checks, setChecks] = useState({ c1: true, c2: true, c3: true });
  const [toggles, setToggles] = useState({ t1: false, t2: false });
  const [comms, setComms] = useState({ whatsapp: false, sms: false, email: false });
  const [expanded, setExpanded] = useState({});

  const flipCheck = k => setChecks(p => ({ ...p, [k]: !p[k] }));
  const flipToggle = k => setToggles(p => ({ ...p, [k]: !p[k] }));
  const flipComm = k => setComms(p => ({ ...p, [k]: !p[k] }));
  const flipExpand = k => setExpanded(p => ({ ...p, [k]: !p[k] }));
  const allRequired = checks.c1 && checks.c2 && checks.c3;

  const REQUIRED_ITEMS = [
    { key: "c1", icon: "❤️", title: "1. Care Assessment Personalization", desc: "Use my answers to personalize my women's health care Assessment." },
    { key: "c2", icon: "👥", title: "2. Clinic Care Team Access", desc: "Allow the clinic's authorized care team to view my submitted information." },
    { key: "c3", icon: "📄", title: "3. Privacy Notice Acknowledgement", desc: "I have read and understand the privacy notice." },
  ];
  const OPTIONAL_ITEMS = [
    { key: "t1", icon: "💬", title: "4. Communication Preferences", desc: "Receive appointment reminders and care updates." },
    { key: "t2", icon: "🔗", title: "5. Report Sharing with Partners", desc: "Allow sharing of uploaded reports with selected care partners." },
  ];

  const ConsentRow = ({ item, isToggle }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <IconCircle size={34}>{item.icon}</IconCircle>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: 600, fontSize: 11, color: T.heading, marginBottom: 2 }}>{item.title}</p>
        <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.4, marginBottom: 2 }}>{item.desc}</p>
        {item.key === "t1" && (
          <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
            {[{ k: "whatsapp", label: "WhatsApp", icon: "📱" }, { k: "sms", label: "SMS", icon: "💬" }, { k: "email", label: "Email", icon: "✉️" }].map(ch => (
              <label key={ch.k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.sub, cursor: "pointer" }}>
                <span>{ch.icon}</span>{ch.label}
                <input type="checkbox" checked={comms[ch.k] || false} onChange={() => flipComm(ch.k)} style={{ marginLeft: 2 }} />
              </label>
            ))}
          </div>
        )}
        <button onClick={() => flipExpand(item.key)} style={{ fontSize: 10, fontWeight: 500, color: T.purple700, background: "none", border: "none", padding: 0, cursor: "pointer", marginTop: 2 }}>
          View details {expanded[item.key] ? "▲" : "▾"}
        </button>
        {expanded[item.key] && (
          <p style={{ fontSize: 10, marginTop: 4, lineHeight: 1.4, color: T.sub }}>Your data is handled in compliance with applicable privacy laws and used solely to improve your care experience.</p>
        )}
      </div>
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        {isToggle ? <Toggle on={toggles[item.key]} onChange={() => flipToggle(item.key)} /> : <CareCheckbox checked={checks[item.key]} onChange={() => flipCheck(item.key)} />}
      </div>
    </div>
  );

  return (
    <PageWrap>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 4 }}>Consent & Privacy</h1>
      <p style={{ fontSize: 11, color: T.sub, marginBottom: 12 }}>Before we personalize your care Assessment, please review how your information will be used.</p>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: "0 14px 6px", marginBottom: 10, border: `1px solid ${T.border}` }}>
            <SectionLabel>Required</SectionLabel>
            {REQUIRED_ITEMS.map(item => <ConsentRow key={item.key} item={item} isToggle={false} />)}
          </div>
          <div style={{ background: "#fff", borderRadius: 12, padding: "0 14px 6px", border: `1px solid ${T.border}` }}>
            <SectionLabel>Optional</SectionLabel>
            {OPTIONAL_ITEMS.map(item => <ConsentRow key={item.key} item={item} isToggle={true} />)}
          </div>
        </div>
        <div style={{ width: 220, flexShrink: 0 }}>
          <RightCard>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading }}>Your privacy is our priority</p>
            <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0 }}>We collect only what's necessary to provide safe, personalized care for you.</p>
            <FeatureItem icon="🛡️" title="Secure & Encrypted" desc="Your data is encrypted and stored securely." />
            <FeatureItem icon="🔒" title="Private by Design" desc="Access limited to authorized care team members only." />
            <FeatureItem icon="👤" title="You're in Control" desc="Manage or withdraw optional consent anytime." />
            <InfoBox>You can manage optional consent later from your profile.</InfoBox>
            <div style={{ textAlign: "center", fontSize: 48, userSelect: "none" }}>🛡️</div>
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={null}
        right={<BtnContinue disabled={!allRequired} onClick={onNext} label="Continue Securely" />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 3 — New Patient Profile
// ─────────────────────────────────────────────────────────────────────────────
export function NewPatientScreen({ onNext, onBack }) {
  const [tab, setTab] = useState("new");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp]; next[i] = val.slice(-1); setOtp(next);
    if (val && i < 5) document.getElementById(`otp-${i + 1}`)?.focus();
  };
  const canContinue = tab === "new" ? name.trim().length > 0 && phone.length === 10 : phone.length === 10;

  const inputSt = {
    width: "100%", border: `1.5px solid ${T.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 12, color: T.heading, outline: "none",
    background: T.inputBg, boxSizing: "border-box",
  };

  return (
    <PageWrap bg={T.lavenderBg}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ maxWidth: 420 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 4 }}>Create New Patient Profile</h1>
          <p style={{ fontSize: 11, color: T.sub, margin: 0 }}>Let's get you set up to personalize your care Assessment.</p>
        </div>
        <div style={{ fontSize: 60, marginTop: -8, flexShrink: 0, userSelect: "none" }}>📋</div>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", borderRadius: 8, padding: 3, background: T.purpleLight, marginBottom: 14 }}>
              {[{ key: "existing", label: "Existing Patient", icon: "👤" }, { key: "new", label: "New Patient", icon: "➕" }].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  flex: 1, padding: "8px 0", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: tab === t.key ? T.purpleBtn : "transparent",
                  color: tab === t.key ? "#fff" : T.purple700, border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5, transition: "all 0.2s",
                }}>
                  <span>{t.icon}</span>{t.label}
                </button>
              ))}
            </div>
            {tab === "new" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 5, color: T.heading }}>Full Name</label>
                <input style={inputSt} placeholder="Enter your full name" value={name} onChange={e => setName(e.target.value)} />
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 3, color: T.heading }}>Mobile Number</label>
              <p style={{ fontSize: 10, color: T.muted, marginBottom: 6 }}>We'll send a one-time password (OTP) to verify your number</p>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", padding: "0 10px", border: `1.5px solid ${T.border}`, borderRadius: 8, fontSize: 11, flexShrink: 0, height: 38 }}>🇮🇳 +91 ▾</div>
                <input style={{ ...inputSt, flex: 1 }} placeholder="10-digit mobile number" value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} />
              </div>
            </div>
            {!otpSent ? (
              <BtnContinue wide disabled={phone.length !== 10} onClick={() => setOtpSent(true)} label="Send OTP to Verify Number" />
            ) : (
              <>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 8, color: T.heading }}>Enter OTP</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {otp.map((v, i) => (
                    <input key={i} id={`otp-${i}`}
                      style={{ width: 44, height: 52, textAlign: "center", fontWeight: 700, fontSize: 18, border: `1.5px solid ${v ? T.purpleBorder : T.border}`, borderRadius: 8, color: T.purple700, background: "#fff", outline: "none" }}
                      maxLength={1} value={v} placeholder="—"
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => { if (e.key === "Backspace" && !v && i > 0) document.getElementById(`otp-${i - 1}`)?.focus(); }}
                    />
                  ))}
                </div>
                <button style={{ fontSize: 11, marginTop: 8, color: T.purple700, background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 500 }}>Resend OTP</button>
              </>
            )}
          </div>
        </div>
        <div style={{ width: 220, flexShrink: 0 }}>
          <RightCard>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading }}>{tab === "new" ? "New Patient Benefits" : "Welcome Back"}</p>
            {tab === "new" ? (
              <ul style={{ fontSize: 10, lineHeight: 1.6, color: T.sub, paddingLeft: 14, margin: 0 }}>
                {["Create your profile and book appointments in seconds.", "All your health records in one place.", "Get personalized care recommendations.", "Track your health Assessment over time."].map((b, i) => <li key={i} style={{ marginBottom: 4 }}>{b}</li>)}
              </ul>
            ) : (
              <p style={{ fontSize: 10, lineHeight: 1.5, color: T.sub, margin: 0 }}>Welcome back! Enter your registered mobile number to access your existing health profile.</p>
            )}
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={<FooterNote>You can update or confirm your details in the next step.</FooterNote>}
        right={<BtnContinue disabled={!canContinue} onClick={onNext} />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 4 — Profile
// ─────────────────────────────────────────────────────────────────────────────
export function ProfileScreen({ onNext, onBack }) {
  const [age, setAge] = useState(null);
  const [marital, setMarital] = useState(null);
  const [occupation, setOccupation] = useState(null);
  const canContinue = age && marital && occupation;

  return (
    <PageWrap>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 4 }}>Tell us about yourself</h1>
          <p style={{ fontSize: 11, color: T.sub, margin: 0 }}>This helps us personalize your care recommendations.</p>
        </div>
        <div style={{ fontSize: 60, marginTop: -8, flexShrink: 0, userSelect: "none" }}>👩</div>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.heading, marginBottom: 8 }}>Age Group</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {AGE_OPTIONS.map(a => <Chip key={a} label={a} selected={age === a}
                // onClick={() => setAge(a)}
                onClick={() => {
                  setAge(a);
                  // get existing data
                  const existing =
                    JSON.parse(localStorage.getItem("patientPersona")) || {};
                  // update only required fields
                  const updated = {
                    ...existing,
                    age: a,
                    ageGroup: a, // if both should stay same
                    savedAt: Date.now(),
                  };

                  // save back
                  localStorage.setItem(
                    "patientPersona",
                    JSON.stringify(updated)
                  );
                }}

              />)}
            </div>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.heading, marginBottom: 8 }}>Marital Status</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {MARITAL_OPTIONS.map(m => <Chip key={m} label={m} selected={marital === m}
                // onClick={() => setMarital(m)} 
                onClick={() => {
                  setMarital(m);

                  const existing =
                    JSON.parse(localStorage.getItem("patientPersona")) || {};

                  const updated = {
                    ...existing,
                    marital: m,
                    maritalStatus: m,
                    savedAt: Date.now(),
                  };

                  localStorage.setItem(
                    "patientPersona",
                    JSON.stringify(updated)
                  );
                }}
              />)}
            </div>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.heading, marginBottom: 8 }}>Occupation / Daily Routine</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {OCCUPATION_OPTIONS.map(o => <Chip key={o} label={o} selected={occupation === o} onClick={() => setOccupation(o)} />)}
            </div>
            <div style={{ background: T.purpleLight, borderRadius: 8, padding: "8px 12px" }}>
              <p style={{ fontSize: 10, color: T.sub, margin: 0 }}>ℹ️ We provide specialized care for every stage of life. You can change this anytime.</p>
            </div>
          </div>
        </div>
        <div style={{ width: 220, flexShrink: 0 }}>
          <RightCard>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading }}>Why we ask this</p>
            <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0 }}>Your age and life stage help us map you to the right care Assessment — from adolescence to postmenopause.</p>
            <FeatureItem icon="🎯" title="Life stage mapping" desc="Care tailored to where you are right now" />
            <FeatureItem icon="🔄" title="Dynamic Assessments" desc="Your Assessment evolves as your stage changes" />
            <FeatureItem icon="🔒" title="Private" desc="Used only for personalization, never shared" />
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={null}
        right={<BtnContinue disabled={!canContinue} onClick={() => onNext({ age, marital, occupation })} label="Save & Continue" />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 5 — Height & Weight
// ─────────────────────────────────────────────────────────────────────────────
export function HeightWeightScreen({ onNext, onBack }) {
  const [heightIn, setHeightIn] = useState(64);
  const [weightKg, setWeightKg] = useState(60);

  const ft = Math.floor(heightIn / 12);
  const in_ = heightIn % 12;
  const bmi = (weightKg / ((heightIn * 0.0254) ** 2)).toFixed(1);
  const bmiCategory = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Normal" : bmi < 30 ? "Overweight" : "Obese";
  const bmiColor = bmi < 18.5 ? T.amber : bmi < 25 ? T.green : bmi < 30 ? T.amber : T.red;

  const SliderRow = ({ label, value, min, max, onChange, display }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: T.heading }}>{label}</label>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.purple700 }}>{display}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: T.purple700, height: 4 }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: 10, color: T.muted }}>{min}</span>
        <span style={{ fontSize: 10, color: T.muted }}>{max}</span>
      </div>
    </div>
  );

  return (
    <PageWrap>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 4 }}>Height & Weight</h1>
          <p style={{ fontSize: 11, color: T.sub, margin: 0 }}>Used to calculate your BMI and personalize recommendations.</p>
        </div>
        <div style={{ fontSize: 60, marginTop: -8, flexShrink: 0, userSelect: "none" }}>⚖️</div>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
            <SliderRow label="Height" value={heightIn} min={48} max={84} onChange={setHeightIn} display={`${ft}.${in_ === 0 ? "0" : in_} Ft`} />
            <SliderRow label="Weight" value={weightKg} min={30} max={150} onChange={setWeightKg} display={`${weightKg} Kg`} />
            <div style={{ background: "#F9FAFB", border: `1.5px solid ${T.border}`, borderRadius: 10, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontSize: 10, color: T.muted, marginBottom: 4 }}>Your BMI</p>
                <p style={{ fontSize: 32, fontWeight: 700, color: bmiColor, margin: 0 }}>{bmi}</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ padding: "4px 12px", borderRadius: 99, fontSize: 11, fontWeight: 600, background: bmiColor + "22", color: bmiColor }}>{bmiCategory}</span>
                <p style={{ fontSize: 10, color: T.muted, margin: "6px 0 0" }}>Based on WHO standards</p>
              </div>
            </div>
          </div>
        </div>
        <div style={{ width: 220, flexShrink: 0 }}>
          <RightCard>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading }}>Why BMI matters</p>
            <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0 }}>BMI is a useful screening tool, but just one part of the picture. We combine it with your life stage and symptoms for a holistic view.</p>
            {[{ label: "Underweight", range: "< 18.5", color: T.amber }, { label: "Normal", range: "18.5 – 24.9", color: T.green }, { label: "Overweight", range: "25 – 29.9", color: T.amber }, { label: "Obese", range: "≥ 30", color: T.red }].map(b => (
              <div key={b.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 10px", borderRadius: 6, background: b.label === bmiCategory ? b.color + "20" : "transparent" }}>
                <span style={{ fontSize: 10, fontWeight: b.label === bmiCategory ? 700 : 400, color: b.label === bmiCategory ? b.color : T.sub }}>{b.label}</span>
                <span style={{ fontSize: 10, color: T.muted }}>{b.range}</span>
              </div>
            ))}
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={null}
        right={<BtnContinue onClick={() => onNext({ heightIn, weightKg, bmi })} label="Save & Continue" />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 6 — Basic Life Stage Questions
// ─────────────────────────────────────────────────────────────────────────────
export function BasicLifeStageScreen({ onNext, onBack }) {
  const [answers, setAnswers] = useState({});
  const [lmp, setLmp] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const setAns = (key, val) => setAnswers(p => ({ ...p, [key]: val }));

  const QUESTIONS = [
    { num: 1, key: "periods_started", q: "Have your periods started?", desc: "Helps us understand your reproductive stage", type: "yesno" },
    { num: 2, key: "currently_pregnant", q: "Are you currently pregnant?", desc: "Helps us understand your reproductive stage", type: "yesno" },
    { num: 3, key: "trying_pregnant", q: "Are you trying to become pregnant?", desc: "Helps us provide the right guidance", type: "yesno" },
    { num: 4, key: "menopause", q: "Have you reached menopause?", desc: "Helps us tailor recommendations", type: "yesno" },
    { num: 5, key: "lmp", q: "Last menstrual period (LMP)", desc: "First day of your last period", type: "date", setter: setLmp, val: lmp },
    { num: 5, key: "delivered_recently", q: "Have you delivered recently?", desc: "Helps us understand your postpartum needs", type: "yesno" },
    { num: 6, key: "last_delivery", q: "When was your last delivery?", desc: "Helps us tailor your postpartum care", type: "date", setter: setDeliveryDate, val: deliveryDate },
  ];

  return (
    <PageWrap>
      <div style={{ marginBottom: 10 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 4 }}>Basic Life-Stage Questions</h1>
        <p style={{ fontSize: 11, color: T.sub, margin: 0 }}>Help us understand your current life stage so we can guide you to the right care path. ⏱️ About 1 minute</p>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: "4px 14px", border: `1px solid ${T.border}` }}>
            {QUESTIONS.map((q, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: idx < QUESTIONS.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <div style={{ width: 26, height: 26, background: T.purple700, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{q.num}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 600, fontSize: 11, color: T.heading, margin: 0 }}>{q.q}</p>
                  <p style={{ fontSize: 10, color: T.muted, margin: 0 }}>{q.desc}</p>
                </div>
                {q.type === "yesno" && (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <YesNoBtn label="Yes" active={answers[q.key] === "Yes"} onClick={() => setAns(q.key, "Yes")} />
                    <YesNoBtn label="No" active={answers[q.key] === "No"} onClick={() => setAns(q.key, "No")} />
                  </div>
                )}
                {q.type === "date" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <input type="date" value={q.val} onChange={e => q.setter(e.target.value)}
                      style={{ border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "5px 10px", fontSize: 11, outline: "none", color: T.heading }} />
                    <span style={{ color: T.muted, fontSize: 14 }}>📅</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div style={{ width: 220, flexShrink: 0 }}>
          <RightCard>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: 80, height: 80, background: "#FFF0F3", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 48, userSelect: "none" }}>👩‍💻</div>
            </div>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading }}>Why we ask these questions</p>
            <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0 }}>Your answers help us understand your current life stage and provide personalized care recommendations.</p>
            <FeatureItem icon="👤" title="Personalized Guidance" desc="Get care that fits your current needs and life stage." />
            <FeatureItem icon="⏱️" title="Better Recommendations" desc="Receive relevant programs, tips, and expert support." />
            <FeatureItem icon="🔒" title="Your Privacy Matters" desc="Your information is secure, confidential, and never shared." />
            <PurpleInfoBox icon="🔒" title="Your data is protected" desc="We use industry-standard encryption to keep your information safe." />
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={<FooterNote>Your data is secure and encrypted</FooterNote>}
        right={<BtnContinue onClick={() => onNext({ ...answers, lmp, deliveryDate })} />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 7 — Entry Reason
// ─────────────────────────────────────────────────────────────────────────────
export function EntryReasonScreen({ profile, onNext, onBack }) {
  const [selected, setSelected] = useState([]);

  const stageKey = getStageKey(profile?.age || "26–35", profile?.marital || "Prefer not to say");
  const Assessment = ENTRY_REASONS_STATIC[stageKey] || ENTRY_REASONS_STATIC.youngAdult;
  const toggle = val => setSelected(p => p.includes(val) ? p.filter(v => v !== val) : [...p, val]);

  const getUrgencyBadge = route => {
    if (!route) return null;
    if (route.toLowerCase().includes("urgent") || route.toLowerCase().includes("triage"))
      return { label: "Urgent", color: T.red, bg: "#FEF2F2" };
    return null;
  };

  const selectedRoute = Assessment.options.find(o => selected.includes(o.value))?.route || null;

  return (
    <PageWrap>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ maxWidth: 480 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 4 }}>{Assessment.screenTitle}</h1>
          <p style={{ fontSize: 11, color: T.sub, marginBottom: 8 }}>Based on your life stage, choose the option that best matches your concern.</p>
          <StageBadge label={Assessment.AssessmentTitle} />
        </div>
        <div style={{ fontSize: 64, marginTop: -8, flexShrink: 0, userSelect: "none" }}>📱</div>
      </div>
      <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1px solid ${T.border}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          {Assessment.options.map(opt => (
            <OptionCard key={opt.value} emoji={opt.emoji} label={opt.label} desc={opt.desc}
              selected={selected.includes(opt.value)} onClick={() => toggle(opt.value)}
              badge={getUrgencyBadge(opt.route)} />
          ))}
        </div>
        <TrustBar />
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <BtnAskNavigator onClick={() => { }} />
            <FooterNote>You can change your response later.</FooterNote>
          </div>
        }
        right={<BtnContinue disabled={selected.length === 0} onClick={() => onNext({ selectedReasons: selected, selectedRoute })} />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 8 — Condition Specific Intake
// ─────────────────────────────────────────────────────────────────────────────
export function ConditionSpecificIntakeScreen({ onNext, onBack, selectedRoute, selectedAssessment, profile }) {
  const route = selectedRoute || "pcos_screening";
  const today = new Date().toISOString().split("T")[0];
  const symptoms = SYMPTOM_MAP[route] || SYMPTOM_MAP.pcos_screening;

  const DURATION_OPTS = ["Less than 1 month", "1–3 months", "3–6 months", "6 months to 1 year", "More than 1 year"];
  const SEVERITY_OPTS = ["Mild", "Moderate", "Severe", "Very severe"];

  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [duration, setDuration] = useState("");
  const [severity, setSeverity] = useState("");
  const [lastPeriod, setLastPeriod] = useState("");
  const [tryingToConceive, setTryingToConceive] = useState("");

  const toggleSymptom = s => setSelectedSymptoms(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const AssessmentLabel = Assessment_LABELS[selectedAssessment] || "Women's Health Assessment";
  const AssessmentEmoji = Assessment_EMOJIS[selectedAssessment] || "🩺";

  return (
    <PageWrap>
      <div style={{ display: "inline-flex", alignItems: "center", background: T.purpleTag, borderRadius: 20, padding: "4px 12px", marginBottom: 14, fontSize: 11, fontWeight: 600, color: T.purple700 }}>
        Condition-Specific Intake
      </div>
      <div style={{ marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ maxWidth: 560 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, marginBottom: 6, lineHeight: 1.2 }}>Tell us a bit more about your symptoms</h1>
          <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>We'll use your answers to personalize your next steps.</p>
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 18, border: `1px solid ${T.border}`, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 13, color: T.heading, margin: 0, marginBottom: 2 }}>What symptoms are you experiencing?</p>
                <p style={{ fontSize: 11, color: T.muted, margin: 0 }}>Select all that apply.</p>
              </div>
              <button style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${T.border}`, background: "#fff", fontSize: 11, color: T.sub, cursor: "pointer", fontWeight: 500, flexShrink: 0 }}>
                ℹ️ Why we ask
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 14 }}>
              {symptoms.map(s => <SymptomChip key={s} label={s} selected={selectedSymptoms.includes(s)} onClick={() => toggleSymptom(s)} />)}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${T.border}` }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.heading, marginBottom: 8 }}>How long has this been happening?</p>
              <StyledSelect value={duration} onChange={setDuration} options={DURATION_OPTS} placeholder="Select duration" />
            </div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${T.border}` }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.heading, marginBottom: 8 }}>Are you trying to conceive? <span style={{ fontSize: 10, color: T.muted, fontWeight: 400 }}>ℹ️</span></p>
              <YesNo value={tryingToConceive} onChange={setTryingToConceive} />
            </div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${T.border}` }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.heading, marginBottom: 8 }}>When was your last period?</p>
              <input type="date" max={today} value={lastPeriod} onChange={e => setLastPeriod(e.target.value)}
                style={{ width: "100%", padding: "10px 36px 10px 12px", borderRadius: 8, border: `1.5px solid ${lastPeriod ? T.purpleBorder : T.border}`, fontSize: 12, color: lastPeriod ? T.heading : T.muted, outline: "none", boxSizing: "border-box", cursor: "pointer" }} />
            </div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${T.border}` }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.heading, marginBottom: 8 }}>How severe are your symptoms? <span style={{ fontSize: 10, color: T.muted, fontWeight: 400 }}>ℹ️</span></p>
              <StyledSelect value={severity} onChange={setSeverity} options={SEVERITY_OPTS} placeholder="Select severity" />
            </div>
          </div>
        </div>
        <div style={{ width: 230, flexShrink: 0 }}>
          <RightCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: 0 }}>Your Assessment</p>
              <button style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.purple700, background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Edit ✏️</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: T.lavenderBg, borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{AssessmentEmoji}</div>
              <p style={{ fontWeight: 700, fontSize: 11, color: T.heading, margin: 0, lineHeight: 1.3 }}>{AssessmentLabel}</p>
            </div>
            <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0 }}>We'll personalize your care plan based on your responses.</p>
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, marginBottom: 6 }}>Current step</p>
              <div style={{ background: T.purple700, color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, display: "inline-block" }}>6 of 10</div>
              <p style={{ fontSize: 10, color: T.muted, margin: "4px 0 0" }}>Condition-Specific Intake</p>
            </div>
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, marginBottom: 2 }}>Est. time to complete</p>
              <p style={{ fontSize: 11, color: T.sub, margin: 0 }}>~5 min</p>
            </div>
            {selectedSymptoms.length > 0 && (
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, marginBottom: 6 }}>Your responses</p>
                <p style={{ fontSize: 11, color: T.sub, margin: "0 0 8px" }}>{selectedSymptoms.length} symptom{selectedSymptoms.length > 1 ? "s" : ""} selected</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {selectedSymptoms.slice(0, 5).map(s => (
                    <div key={s} style={{ width: 30, height: 30, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }} title={s}>{getSymptomIcon(s)}</div>
                  ))}
                  {selectedSymptoms.length > 5 && (
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: T.purpleTag, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: T.purple700 }}>+{selectedSymptoms.length - 5}</div>
                  )}
                </div>
              </div>
            )}
            <div style={{ background: T.lavenderBg, borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>🔒</span>
              <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>Your answers are private and securely encrypted.</p>
            </div>
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={<FooterNote>Your responses help personalize your care Assessment</FooterNote>}
        right={<BtnContinue label="Continue" disabled={selectedSymptoms.length === 0} onClick={() => onNext({ selectedSymptoms, duration, severity, lastPeriod, tryingToConceive })} />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 9 — Safety Check
// ─────────────────────────────────────────────────────────────────────────────
export function SafetyCheckScreen({ onNext, onBack }) {
  const [selected, setSelected] = useState([]);

  const toggle = label => {
    if (label === "None of these") { setSelected(["None of these"]); return; }
    setSelected(prev => {
      const without = prev.filter(x => x !== "None of these");
      return without.includes(label) ? without.filter(x => x !== label) : [...without, label];
    });
  };

  const hasUrgent = selected.length > 0 && !selected.includes("None of these");
  const URGENCY = hasUrgent
    ? { concern: "Possible urgent symptoms reported", action: "Priority review needed", response: "We may guide you to same-day care", concernColor: T.red, actionColor: T.amber, responseColor: T.purple700 }
    : { concern: "No urgent symptoms reported", action: "Standard care pathway", response: "We'll guide you through personalized care", concernColor: T.green, actionColor: T.green, responseColor: T.purple700 };

  return (
    <PageWrap>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: hasUrgent ? "#FEE2E2" : T.pinkLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 12px", border: `2px solid ${hasUrgent ? "#FCA5A5" : T.pinkBorder}` }}>
          {hasUrgent ? "⚠️" : "🛡️"}
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 6 }}>Before we continue, let's make sure you're safe</h1>
        <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>Please tell us if you are experiencing any urgent symptoms right now.</p>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 16, border: `1px solid ${T.border}`, marginBottom: 14 }}>
            <p style={{ fontWeight: 700, fontSize: 13, color: T.heading, marginBottom: 4 }}>Are you currently experiencing any of these?</p>
            <p style={{ fontSize: 11, color: T.muted, marginBottom: 14 }}>Select all that apply.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {SAFETY_SYMPTOMS.map(({ label, icon }) => {
                const isSel = selected.includes(label);
                const isNone = label === "None of these";
                const isUrg = isSel && !isNone;
                return (
                  <button key={label} onClick={() => toggle(label)} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                    border: isSel ? `2px solid ${isUrg ? "#FCA5A5" : isNone ? T.green : T.purpleBorder}` : `1.5px solid ${T.border}`,
                    background: isSel ? isUrg ? "#FEF2F2" : isNone ? "#ECFDF5" : T.purpleLight : "#fff",
                  }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: isSel ? isUrg ? "#FEE2E2" : isNone ? "#D1FAE5" : T.purpleLight : "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{icon}</div>
                    <span style={{ fontSize: 12, fontWeight: isSel ? 600 : 400, color: isSel ? isUrg ? "#991B1B" : isNone ? "#065F46" : T.purple700 : T.body, flex: 1 }}>{label}</span>
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: isSel ? "none" : `1.5px solid ${T.border}`, background: isSel ? isUrg ? T.red : isNone ? T.green : T.purple700 : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {isSel && <svg width="10" height="8" viewBox="0 0 12 9" fill="none"><path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 12, display: "flex", alignItems: "flex-start", gap: 8, background: T.infoBg, borderRadius: 8, padding: "8px 10px", border: `1px solid ${T.infoBorder}` }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>ℹ️</span>
              <p style={{ fontSize: 10, color: T.infoText, margin: 0, lineHeight: 1.5 }}>If you are pregnant, postpartum, or have severe symptoms, our care team may recommend urgent evaluation.</p>
            </div>
          </div>
          <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${hasUrgent ? "#FCA5A5" : T.border}` }}>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, marginBottom: 10 }}>Urgency Check</p>
            {[
              { icon: "⚠️", label: "Current concern", value: URGENCY.concern, color: URGENCY.concernColor },
              { icon: "🔶", label: "Suggested action", value: URGENCY.action, color: URGENCY.actionColor },
              { icon: "💜", label: "Care team response", value: URGENCY.response, color: URGENCY.responseColor },
            ].map(row => (
              <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14 }}>{row.icon}</span>
                  <span style={{ fontSize: 11, color: T.body, fontWeight: 500 }}>{row.label}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: row.color, textAlign: "right", maxWidth: 200 }}>{row.value}</span>
              </div>
            ))}
            {hasUrgent && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "flex-start", gap: 8, background: "#FFFBEB", borderRadius: 8, padding: "8px 10px", border: `1px solid #FDE68A` }}>
                <span style={{ fontSize: 14 }}>⚠️</span>
                <p style={{ fontSize: 10, color: "#92400E", margin: 0, lineHeight: 1.5 }}>This screen does not provide a diagnosis. If symptoms are severe, seek urgent medical care.</p>
              </div>
            )}
          </div>
        </div>
        <div style={{ width: 230, flexShrink: 0 }}>
          <RightCard>
            <div style={{ textAlign: "center", fontSize: 48 }}>🛡️</div>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, textAlign: "center" }}>Your safety comes first</p>
            <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0, textAlign: "center" }}>This quick check helps us identify if you need immediate attention before proceeding with your care Assessment.</p>
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              {[{ icon: "🚨", title: "Urgent symptoms", desc: "We may direct you to same-day or emergency care." }, { icon: "🩺", title: "Non-urgent", desc: "We'll continue personalizing your care Assessment." }, { icon: "🔒", title: "Secure & Private", desc: "Reviewed only by authorized care team members." }].map(f => (
                <div key={f.title} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{f.icon}</div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 11, color: T.heading, margin: "0 0 2px" }}>{f.title}</p>
                    <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center", fontSize: 10, color: T.muted, paddingTop: 4, borderTop: `1px solid ${T.border}` }}>🔒 Secure · Private · Reviewed by care team</div>
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={onBack} />}
        center={null}
        right={<BtnContinue label={hasUrgent ? "⚠️ Continue — I Understand" : "Continue"} color={hasUrgent ? T.red : T.purpleBtn} disabled={selected.length === 0} onClick={() => onNext({ urgentSymptoms: selected, hasUrgent })} />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 10 — Adaptive Questionnaire
// ─────────────────────────────────────────────────────────────────────────────
export function AdaptiveQuestionnaireScreen({ onNext, onBack, allData = {} }) {
  const questions = buildAdaptiveQuestions(allData);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);

  const PER_PAGE = 2;
  const totalPages = Math.ceil(questions.length / PER_PAGE);
  const pageQs = questions.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  const setAns = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));
  const toggleAns = (id, val) => setAnswers(prev => {
    const cur = prev[id] || [];
    return { ...prev, [id]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
  });

  const pageAnswered = pageQs.every(q => {
    const a = answers[q.id];
    return q.type === "multi" ? a && a.length > 0 : !!a;
  });

  const answered = questions.filter(q => !!answers[q.id] && (q.type !== "multi" || answers[q.id]?.length > 0)).length;
  const progressPct = Math.round((answered / questions.length) * 100);

  const handleNext = () => {
    if (page < totalPages - 1) setPage(p => p + 1);
    else onNext(answers);
  };

  return (
    <PageWrap>
      <div style={{ display: "inline-flex", alignItems: "center", background: T.purpleTag, borderRadius: 20, padding: "4px 12px", marginBottom: 14, fontSize: 11, fontWeight: 600, color: T.purple700 }}>
        Adaptive Questionnaire
      </div>
      <div style={{ marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ maxWidth: 560 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, marginBottom: 6 }}>A few more personalised questions</h1>
          <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>Based on your responses, we've tailored these questions to build the most accurate care plan for you.</p>
        </div>
        <div style={{ fontSize: 56, userSelect: "none", marginTop: -8 }}>🎯</div>
      </div>
      <div style={{ background: T.border, borderRadius: 99, height: 6, marginBottom: 20, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${progressPct}%`, background: `linear-gradient(90deg, ${T.purple700}, ${T.purple500})`, borderRadius: 99, transition: "width 0.4s ease" }} />
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {pageQs.map((question, idx) => {
            const val = answers[question.id];
            const globalIdx = page * PER_PAGE + idx;
            return (
              <div key={question.id} style={{ background: "#fff", borderRadius: 14, padding: 18, border: `1px solid ${val ? T.purpleBorder : T.border}`, transition: "border-color 0.2s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: val ? T.purple700 : T.purpleLight, color: val ? "#fff" : T.purple700, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, transition: "all 0.2s" }}>
                    {val ? "✓" : globalIdx + 1}
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.heading, margin: 0, lineHeight: 1.3 }}>{question.q}</p>
                  {question.type === "multi" && <span style={{ fontSize: 10, color: T.muted, background: "#F3F4F6", borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>Multi-select</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {question.options.map(opt => {
                    const active = question.type === "multi" ? (val || []).includes(opt) : val === opt;
                    return (
                      <button key={opt} onClick={() => question.type === "multi" ? toggleAns(question.id, opt) : setAns(question.id, opt)} style={{
                        padding: "8px 16px", borderRadius: 999, fontSize: 12, cursor: "pointer", transition: "all 0.15s",
                        border: active ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
                        background: active ? T.purpleLight : "#fff",
                        color: active ? T.purple700 : T.sub, fontWeight: active ? 600 : 400,
                      }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ width: 220, flexShrink: 0 }}>
          <RightCard>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, marginBottom: 2 }}>Your progress</p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "8px 0" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: T.purpleLight, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: `4px solid ${T.purple700}` }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: T.purple700 }}>{progressPct}%</span>
                <span style={{ fontSize: 9, color: T.muted }}>complete</span>
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, marginBottom: 6 }}>Questions answered</p>
              {questions.map((q, i) => {
                const ans = !!answers[q.id] && (q.type !== "multi" || answers[q.id]?.length > 0);
                return (
                  <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: ans ? T.green : T.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, flexShrink: 0, transition: "background 0.3s" }}>
                      {ans && <svg width="8" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                    <p style={{ fontSize: 10, color: ans ? T.heading : T.muted, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: ans ? 600 : 400 }}>Q{i + 1}: {q.q.slice(0, 28)}…</p>
                  </div>
                );
              })}
            </div>
            <div style={{ background: T.lavenderBg, borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "flex-start", gap: 6 }}>
              <span style={{ fontSize: 14 }}>🎯</span>
              <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>These questions were selected based on your reported symptoms and Assessment type.</p>
            </div>
          </RightCard>
        </div>
      </div>
      <FooterBar
        left={<BtnBack onClick={page === 0 ? onBack : () => setPage(p => p - 1)} />}
        center={<p style={{ fontSize: 10, color: T.muted, margin: 0 }}>Page {page + 1} of {totalPages}</p>}
        right={<BtnContinue label={page < totalPages - 1 ? "Next" : "Complete Questionnaire"} disabled={!pageAnswered} onClick={handleNext} />}
      />
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 11 — Recommendation (Done)
// ─────────────────────────────────────────────────────────────────────────────
export function RecommendationScreen({ onRestart, allData = {} }) {
  const { selectedSymptoms = [], hasUrgent, selectedAssessment } = allData;
  const AssessmentLabel = Assessment_LABELS[selectedAssessment] || "Women's Health";

  return (
    <PageWrap>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "32px 0" }}>
        <div style={{ fontSize: 72, userSelect: "none" }}>🎉</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, margin: "16px 0 8px" }}>Your Care Assessment is Ready!</h1>
        <p style={{ fontSize: 12, color: T.sub, lineHeight: 1.6, marginBottom: 24, maxWidth: 400 }}>
          We've built a personalized care plan based on your profile, symptoms, and responses.
          {hasUrgent && " Given your urgent symptoms, a care navigator will reach out shortly."}
        </p>
        <div style={{ background: T.purpleLight, borderRadius: 12, padding: "10px 20px", marginBottom: 16, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>{Assessment_EMOJIS[selectedAssessment] || "🩺"}</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: T.purple700 }}>{AssessmentLabel}</span>
        </div>
        {selectedSymptoms.length > 0 && (
          <div style={{ background: T.purpleLight, borderRadius: 12, padding: "12px 20px", marginBottom: 20, maxWidth: 400, width: "100%" }}>
            <p style={{ fontWeight: 700, fontSize: 12, color: T.purple700, marginBottom: 8 }}>Symptoms being addressed:</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
              {selectedSymptoms.map(s => (
                <span key={s} style={{ background: "#fff", border: `1px solid ${T.purpleBorder}`, borderRadius: 20, padding: "3px 10px", fontSize: 11, color: T.purple700, fontWeight: 500 }}>
                  {getSymptomIcon(s)} {s}
                </span>
              ))}
            </div>
          </div>
        )}
        <BtnContinue onClick={onRestart} label="Start Over (Demo)" color={T.purpleBtn} />
      </div>
    </PageWrap>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP PROGRESS NAV
// ─────────────────────────────────────────────────────────────────────────────
const SCREEN_ORDER = [
  "start", "consent", "patient", "profile", "heightweight",
  "lifestage", "entry", "condition", "safety", "adaptive", "done"
];

const SCREEN_LABELS = {
  start: "Assessment", consent: "Consent", patient: "Patient",
  profile: "Profile", heightweight: "Height/Wt", lifestage: "Life Stage",
  entry: "Concerns", condition: "Symptoms", safety: "Safety",
  adaptive: "Questions", done: "Done",
};


// ─────────────────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  console.log("Imported config:", config);
  const [screen, setScreen] = useState("start");
  const [allData, setAllData] = useState({
    selectedAssessment: null,
    selectedRoute: null,
    profile: {},
  });

  console.log("Imported config..............................:", config);



 useEffect(() => {
    console.log(allData, "this is the alldata")
  }, [allData])
  if(!config) return null; 

  const merge = d => setAllData(prev => ({ ...prev, ...d }));
  const go = key => setScreen(key);

  const SCREENS = {
    // start: (
    //   <StartCareAssessmentScreen
    //     config={config}
    //     onNext={selectedAssessment => { merge({ selectedAssessment }); go("consent"); }}
    //   />
    // ),
    start: (
  <StartCareAssessmentScreen
    config={config}
    onExit={() => { window.location.href = "/"; }}   // or props.onExitFlow, whatever your app shell expects
    onNext={selectedAssessment => { merge({ selectedAssessment }); go("consent"); }}
  />
),
    consent: (
      <ConsentScreen
        onNext={() => go("patient")}
        onBack={() => go("start")}
      />
    ),
    patient: (
      <NewPatientScreen
        onNext={() => go("profile")}
        onBack={() => go("consent")}
      />
    ),
    profile: (
      <ProfileScreen
        onNext={d => { merge({ profile: { ...allData.profile, ...d } }); go("heightweight"); }}
        onBack={() => go("patient")}
      />
    ),
    heightweight: (
      <HeightWeightScreen
        onNext={d => { merge({ ...d }); go("lifestage"); }}
        onBack={() => go("profile")}
      />
    ),
    lifestage: (
      <BasicLifeStageScreen
        onNext={d => { merge({ lifeStageAnswers: d }); go("entry"); }}
        onBack={() => go("heightweight")}
      />
    ),
    entry: (
      <EntryReasonScreen
        profile={allData.profile}
        onNext={d => { merge({ selectedReasons: d.selectedReasons, selectedRoute: d.selectedRoute }); go("condition"); }}
        onBack={() => go("lifestage")}
      />
    ),
    condition: (
      <ConditionSpecificIntakeScreen
        selectedRoute={allData.selectedRoute}
        selectedAssessment={allData.selectedAssessment}
        profile={allData.profile}
        onNext={d => { merge(d); go("safety"); }}
        onBack={() => go("entry")}
      />
    ),
    safety: (
      <SafetyCheckScreen
        onNext={d => { merge(d); go("adaptive"); }}
        onBack={() => go("condition")}
      />
    ),
    adaptive: (
      <AdaptiveQuestionnaireScreen
        allData={allData}
        onNext={d => { merge({ adaptiveAnswers: d }); go("done"); }}
        onBack={() => go("safety")}
      />
    ),
    done: (
      <RecommendationScreen
        allData={allData}
        onRestart={() => {
          setAllData({ selectedAssessment: null, selectedRoute: null, profile: {} });
          go("start");
        }}
      />
    ),
  };

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div>
        {SCREENS[screen]}
      </div>
    </div>
  );
}

// export function StartCareAssessmentScreen({ onNext, config }) {
//   const [selected, setSelected] = useState(null);
//   const navigate = useNavigate();
// console.log(config,".............")
//   // Guard: config may still be loading at the App root (see useAssessmentConfig)

//   const { title, subtitle, assessmentOptions } = config.screens.start;
//   console.log(title, subtitle, assessmentOptions)
//   if (!config) return null;

//   return (
//     <PageWrap>
//       <ScreenHeaderDFO/>
//       <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", }}>
//         <div style={{ maxWidth: 480 }}>
//           <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, lineHeight: 1.2, marginBottom: 6 }}>{title}</h1>
//           <p style={{ fontSize: 12, color: T.sub, lineHeight: 1.5, marginBottom: "20px" }}>{subtitle}</p>
//         </div>
//       </div>
//       <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1px solid ${T.border}` }}>
//         <p style={{ fontWeight: 600, fontSize: 12, marginBottom: 10, color: T.heading }}>Choose your Assessment</p>
//         <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
//           {assessmentOptions.map(opt => (
//             <OptionCard key={opt.value} emoji={opt.emoji} label={opt.label} desc={opt.desc}
//               selected={selected === opt.value} onClick={() => setSelected(opt.value)} />
//           ))}
//         </div>
//         <TrustBar />
//       </div>
//       <FooterBar
//         left={<BtnBack
//            onClick={() => { navigate('/') }} isExit={true} />}
//         center={<FooterNote>You can change your selected Assessment later.</FooterNote>}
//         right={<BtnContinue onClick={() => onNext(selected)} disabled={!selected} />}
//       />
//     </PageWrap>
//   );
// }


//  previous care assessment
// export function StartCareAssessmentScreen({ onNext, onExit, config }) {
//   const [selected, setSelected] = useState(null);
// console.log(config,"thisis the onnnnnnnnnnnfig")
//   // Guard FIRST — before any destructuring off config
//   if (!config?.screens?.start) return null;

//   const { title, subtitle, assessmentOptions } = config.screens.start;

//   return (
//     <PageWrap>
//       <ScreenHeaderDFO />
//       <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
//         <div style={{ maxWidth: 480 }}>
//           <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, lineHeight: 1.2, marginBottom: 6 }}>{title}</h1>
//           <p style={{ fontSize: 12, color: T.sub, lineHeight: 1.5, marginBottom: "20px" }}>{subtitle}</p>
//         </div>
//       </div>
//       <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1px solid ${T.border}` }}>
//         <p style={{ fontWeight: 600, fontSize: 12, marginBottom: 10, color: T.heading }}>Choose your Assessment</p>
//         <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
//           {assessmentOptions.map(opt => (
//             <OptionCard
//               key={opt.value}
//               emoji={opt.emoji}
//               label={opt.label}
//               desc={opt.desc}
//               selected={selected === opt.value}
//               onClick={() => setSelected(opt.value)}
//             />
//           ))}
//         </div>
//         <TrustBar />
//       </div>
//       <FooterBar
//         left={<BtnBack onClick={onExit} isExit={true} />}
//         center={<FooterNote>You can change your selected Assessment later.</FooterNote>}
//         right={<BtnContinue onClick={() => onNext(selected)} disabled={!selected} />}
//       />
//     </PageWrap>
//   );
// }
export function TrustStrip({ items = [] }) {
  if (items.length === 0) return null;
  return (
    // <div className="grid grid-cols-3 gap-4 border border-[#CE93D8] rounded-2xl p-4 bg-[#F3E5F5] mt-2">
    
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 border border-[#CE93D8] rounded-2xl p-4 bg-[#F3E5F5] mt-2">
      {items.map((t, i) => (
        <div key={i} className="flex items-start gap-3">
          <span className="text-xl">{t.icon}</span>
          <div>
            <div className="text-xs font-bold text-[#38006b] mb-0.5">{t.title}</div>
            <div className="text-xs text-gray-500 leading-relaxed">{t.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
// newly mapped with the json fetching
export function StartCareAssessmentScreen({ onNext }) {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  // Falls back to the static Assessment_OPTIONS constant until the fetch
  // resolves, so the screen never renders empty on first paint.
  const [screenContent, setScreenContent] = useState({
    screenTitle: "Start Care Assessment",
    screenSubtitle: "Choose the care path that best matches your current need. We'll guide you step by step.",
    options: Assessment_OPTIONS,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStartAssessment() {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await fetch(`${BASE_URL}/api/femcare/lifestages/start-assessment/?clinic=femcare`);
        if (!res.ok) throw new Error(`Failed to load assessment options (${res.status})`);
        const data = await res.json();
        if (cancelled) return;

        setScreenContent({
          screenTitle: data.screenTitle || "Start Care Assessment",
          screenSubtitle: data.screenSubtitle || "",
          options: Array.isArray(data.options) && data.options.length > 0 ? data.options : Assessment_OPTIONS,
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) setLoadError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchStartAssessment();
    return () => { cancelled = true; };
  }, []);

  return (
    <PageWrap>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ maxWidth: 480 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, lineHeight: 1.2, marginBottom: 6 }}>
            {screenContent.screenTitle}
          </h1>
          <p style={{ fontSize: 12, color: T.sub, lineHeight: 1.5, margin: 0 }}>
            {screenContent.screenSubtitle}
          </p>
        </div>
        <div style={{ fontSize: 64, marginTop: -8, flexShrink: 0, userSelect: "none" }}>👩‍⚕️</div>
      </div>
      <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1px solid ${T.border}` }}>
        <p style={{ fontWeight: 600, fontSize: 12, marginBottom: 10, color: T.heading }}>Choose your Assessment</p>

        {loading && (
          <p style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>Loading options…</p>
        )}
        {loadError && (
          <p style={{ fontSize: 11, color: T.red, marginBottom: 8 }}>
            Couldn't load the latest options — showing defaults.
          </p>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          {screenContent.options.map(opt => (
            <OptionCard key={opt.value} emoji={opt.emoji} label={opt.label} desc={opt.desc}
              selected={selected === opt.value} onClick={() => setSelected(opt.value)} />
          ))}
        </div>
        <TrustStrip/>
      </div>
      <FooterBar
        left={<BtnBack onClick={() => { navigate('/') }} isExit={true} />}
        center={<FooterNote>You can change your selected Assessment later.</FooterNote>}
        right={<BtnContinue disabled={!selected} onClick={() => onNext(selected)} />}
      />
    </PageWrap>
  );
}